import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import {
  Code, Save, Facebook, BarChart3,
  Tag, Plus, Trash2, Check, AlertCircle,
  Zap, Shield, ToggleLeft, ToggleRight, Send,
  Activity, RefreshCw, Filter, Clock, X,
  FlaskConical, Play, Copy, ChevronDown, ChevronUp
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import AdminLayout from "./AdminLayout";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Global Meta Configuration Section ───
const MetaGlobalConfig = () => {
  const [config, setConfig] = useState({
    enabled: false,
    pixel_id: "",
    access_token_masked: "",
    has_access_token: false,
    test_event_code: "",
    api_version: "v21.0",
    app_base_url: "",
  });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [testing, setTesting] = useState(false);
  const [testResult, setTestResult] = useState(null);
  const [healthStatus, setHealthStatus] = useState(null);

  useEffect(() => {
    fetchConfig();
    fetchHealth();
  }, []);

  const fetchHealth = async () => {
    try {
      const res = await axios.get(`${API}/admin/meta-tracking-status`);
      setHealthStatus(res.data);
    } catch { /* ignore */ }
  };

  const fetchConfig = async () => {
    try {
      const res = await axios.get(`${API}/admin/meta-settings`);
      setConfig(res.data);
    } catch {
      toast.error("Failed to load Meta settings");
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/meta-settings`, {
        enabled: config.enabled,
        api_version: config.api_version,
      });
      toast.success("Settings saved");
      setTestResult(null);
      fetchHealth();
    } catch {
      toast.error("Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const testConnection = async () => {
    setTesting(true);
    setTestResult(null);
    try {
      const res = await axios.post(`${API}/admin/meta-settings/test`);
      setTestResult(res.data);
      if (res.data.success) {
        toast.success("Test event sent successfully");
        fetchHealth();
      } else {
        toast.error(res.data.error || "Test failed");
      }
    } catch {
      toast.error("Connection test failed");
      setTestResult({ success: false, error: "Request failed" });
    } finally {
      setTesting(false);
    }
  };

  const isConfigured = config.pixel_id && config.has_access_token;

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent" />
      </div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden"
    >
      {/* Header */}
      <div className="p-6 border-b border-[#27272A] flex flex-col sm:flex-row justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-lg bg-[#1877F2]/10 flex items-center justify-center">
            <Facebook className="w-5 h-5 text-[#1877F2]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#F5F5F0]" data-testid="meta-config-title">Meta Pixel & Conversions API</h2>
            <p className="text-xs text-[#71717A]">Global configuration for all landing pages</p>
          </div>
        </div>
        <div className="flex items-center gap-3">
          {/* Status badge */}
          <span
            data-testid="meta-status-badge"
            className={`px-3 py-1 rounded-full text-xs font-medium ${
              config.enabled && isConfigured
                ? "bg-green-500/10 text-green-400 border border-green-500/20"
                : "bg-[#27272A] text-[#71717A] border border-[#3f3f46]"
            }`}
          >
            {config.enabled && isConfigured ? "Active" : "Inactive"}
          </span>
          {/* Enable/Disable toggle */}
          <button
            data-testid="meta-toggle-btn"
            onClick={() => setConfig(prev => ({ ...prev, enabled: !prev.enabled }))}
            className={`flex items-center gap-2 px-3 py-1.5 rounded text-sm font-medium transition-colors ${
              config.enabled
                ? "bg-green-500/10 text-green-400 hover:bg-green-500/20"
                : "bg-[#27272A] text-[#71717A] hover:bg-[#3f3f46]"
            }`}
          >
            {config.enabled ? <ToggleRight className="w-4 h-4" /> : <ToggleLeft className="w-4 h-4" />}
            {config.enabled ? "Enabled" : "Disabled"}
          </button>
        </div>
      </div>

      {/* Health Status Bar */}
      {healthStatus && config.enabled && isConfigured && (
        <div className="px-6 py-3 border-b border-[#1a1a1a] flex flex-wrap items-center gap-4" data-testid="meta-health-bar">
          <span className="text-xs text-[#52525B]">Last 24h:</span>
          <span className="text-xs text-[#A1A1AA]">
            <strong className="text-[#F5F5F0]">{healthStatus.last_24h?.total || 0}</strong> events
          </span>
          <span className="text-xs text-green-400">
            {healthStatus.last_24h?.success || 0} sent
          </span>
          {(healthStatus.last_24h?.failed || 0) > 0 && (
            <span className="text-xs text-red-400">
              {healthStatus.last_24h.failed} failed
            </span>
          )}
          {healthStatus.event_breakdown?.length > 0 && (
            <div className="flex items-center gap-2 ml-auto">
              {healthStatus.event_breakdown.slice(0, 4).map(e => (
                <span key={e.event} className="px-2 py-0.5 rounded bg-[#27272A] text-[#71717A] text-xs font-mono">
                  {e.event} ({e.count})
                </span>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Fields */}
      <div className="p-6 space-y-5">
        {/* Source indicator */}
        <div className="flex items-center gap-2 px-3 py-2 bg-[#0A0A0A] border border-[#27272A] rounded-md">
          <Shield className="w-4 h-4 text-[#D4AF37]" />
          <span className="text-xs text-[#A1A1AA]">
            Secrets are managed via <strong className="text-[#F5F5F0]">Settings &gt; Environment Variables</strong>. 
            Toggle and API version are saved here.
          </span>
        </div>

        {/* Pixel ID (read-only from env var) */}
        <div>
          <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 flex items-center gap-2">
            META_PIXEL_ID
            {config.pixel_id ? (
              <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-xs border border-green-500/20">Set</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-xs border border-red-500/20">Not Set</span>
            )}
          </label>
          <input
            data-testid="meta-pixel-id-input"
            type="text"
            value={config.pixel_id || "Not configured"}
            readOnly
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] rounded-md font-mono cursor-not-allowed"
          />
        </div>

        {/* Access Token (read-only from env var) */}
        <div>
          <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 flex items-center gap-2">
            META_CAPI_ACCESS_TOKEN
            {config.has_access_token ? (
              <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-xs border border-green-500/20">Set</span>
            ) : (
              <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-xs border border-red-500/20">Not Set</span>
            )}
          </label>
          <input
            data-testid="meta-access-token-input"
            type="password"
            value={config.access_token_masked || "Not configured"}
            readOnly
            className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] rounded-md font-mono cursor-not-allowed"
          />
        </div>

        {/* Row: Test Event Code (read-only) + API Version (editable) + APP_BASE_URL (read-only) */}
        <div className="grid sm:grid-cols-3 gap-5">
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 flex items-center gap-2">
              META_TEST_EVENT_CODE
              {config.test_event_code ? (
                <span className="px-1.5 py-0.5 rounded bg-amber-500/10 text-amber-400 text-xs border border-amber-500/20">Active</span>
              ) : (
                <span className="px-1.5 py-0.5 rounded bg-[#27272A] text-[#52525B] text-xs border border-[#3f3f46]">Off</span>
              )}
            </label>
            <input
              data-testid="meta-test-code-input"
              type="text"
              value={config.test_event_code || "Not set"}
              readOnly
              className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] rounded-md font-mono cursor-not-allowed text-sm"
            />
          </div>
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 block">API Version</label>
            <select
              data-testid="meta-api-version-select"
              value={config.api_version}
              onChange={(e) => setConfig(prev => ({ ...prev, api_version: e.target.value }))}
              className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-[#D4AF37] transition-colors"
            >
              <option value="v21.0">v21.0 (Recommended)</option>
              <option value="v20.0">v20.0</option>
              <option value="v19.0">v19.0</option>
            </select>
          </div>
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 flex items-center gap-2">
              APP_BASE_URL
              {config.app_base_url ? (
                <span className="px-1.5 py-0.5 rounded bg-green-500/10 text-green-400 text-xs border border-green-500/20">Set</span>
              ) : (
                <span className="px-1.5 py-0.5 rounded bg-red-500/10 text-red-400 text-xs border border-red-500/20">Not Set</span>
              )}
            </label>
            <input
              type="text"
              value={config.app_base_url || "Not configured"}
              readOnly
              className="w-full px-4 py-2.5 bg-[#0A0A0A] border border-[#27272A] text-[#71717A] rounded-md font-mono cursor-not-allowed text-sm"
            />
          </div>
        </div>

        {/* Test Result */}
        {testResult && (
          <div
            data-testid="meta-test-result"
            className={`p-4 rounded-md border text-sm ${
              testResult.success
                ? "bg-green-500/5 border-green-500/20 text-green-300"
                : "bg-red-500/5 border-red-500/20 text-red-300"
            }`}
          >
            {testResult.success ? (
              <div className="flex items-center gap-2">
                <Check className="w-4 h-4 shrink-0" />
                <span>Test PageView event sent successfully. Events received: {testResult.events_received}. Check Meta Events Manager to confirm.</span>
              </div>
            ) : (
              <div className="flex items-start gap-2">
                <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>{testResult.error || testResult.reason || "Connection failed. Check your Pixel ID and Access Token."}</span>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Actions */}
      <div className="px-6 py-4 border-t border-[#27272A] flex flex-col sm:flex-row items-stretch sm:items-center gap-3 justify-end">
        <button
          data-testid="meta-test-btn"
          onClick={testConnection}
          disabled={testing || !config.pixel_id || !config.has_access_token}
          className="flex items-center justify-center gap-2 px-4 py-2 text-sm border border-[#27272A] text-[#A1A1AA] rounded-md hover:bg-[#1a1a1a] hover:text-[#F5F5F0] transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
        >
          <Send className="w-4 h-4" />
          {testing ? "Sending..." : "Test Connection"}
        </button>
        <button
          data-testid="meta-save-btn"
          onClick={saveConfig}
          disabled={saving}
          className="flex items-center justify-center gap-2 px-5 py-2 text-sm bg-[#D4AF37] text-[#050505] font-semibold rounded-md hover:bg-[#B8943B] transition-colors disabled:opacity-50"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Settings"}
        </button>
      </div>
    </motion.div>
  );
};

// ─── Per-Page Pixel Settings Section ───
const PerPagePixelSettings = () => {
  const [landingPages, setLandingPages] = useState([]);
  const [selectedPage, setSelectedPage] = useState("");
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchLandingPages();
  }, []);

  useEffect(() => {
    if (selectedPage) fetchPixelSettings();
  }, [selectedPage]);

  const fetchLandingPages = async () => {
    try {
      const res = await axios.get(`${API}/admin/event-landing-pages`);
      setLandingPages(res.data);
      if (res.data.length > 0) setSelectedPage(res.data[0].slug);
    } catch {
      toast.error("Failed to load landing pages");
    }
  };

  const fetchPixelSettings = async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/pixel-settings/${selectedPage}`);
      setSettings(res.data);
    } catch {
      toast.error("Failed to load pixel settings");
    } finally {
      setLoading(false);
    }
  };

  const saveSettings = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/pixel-settings/${selectedPage}`, settings);
      toast.success("Page pixel settings saved");
    } catch {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key, value) => setSettings(prev => ({ ...prev, [key]: value }));

  const addCustomScript = () => {
    const scripts = settings?.custom_scripts || [];
    setSettings(prev => ({
      ...prev,
      custom_scripts: [...scripts, { name: "", code: "", location: "head" }]
    }));
  };

  const updateCustomScript = (index, updates) => {
    const scripts = [...(settings?.custom_scripts || [])];
    scripts[index] = { ...scripts[index], ...updates };
    setSettings(prev => ({ ...prev, custom_scripts: scripts }));
  };

  const removeCustomScript = (index) => {
    const scripts = (settings?.custom_scripts || []).filter((_, i) => i !== index);
    setSettings(prev => ({ ...prev, custom_scripts: scripts }));
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.1 }}
      className="space-y-6"
    >
      {/* Page Selector + Save */}
      <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
        <select
          data-testid="page-selector"
          value={selectedPage}
          onChange={(e) => setSelectedPage(e.target.value)}
          className="flex-1 px-4 py-2 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded-md"
        >
          {landingPages.map(page => (
            <option key={page.id || page.slug} value={page.slug}>{page.title || page.slug}</option>
          ))}
        </select>
        <button
          data-testid="page-pixel-save-btn"
          onClick={saveSettings}
          disabled={saving || !settings}
          className="flex items-center justify-center gap-2 px-4 py-2 bg-[#D4AF37] text-[#050505] font-semibold hover:bg-[#B8943B] transition-colors rounded-md disabled:opacity-50 text-sm"
        >
          <Save className="w-4 h-4" />
          {saving ? "Saving..." : "Save Page Settings"}
        </button>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-2 border-[#D4AF37] border-t-transparent" />
        </div>
      ) : settings ? (
        <div className="space-y-6">
          {/* Additional Tracking Pixels (per-page) */}
          <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
            <h3 className="font-semibold text-[#F5F5F0] mb-5 flex items-center gap-2 text-sm">
              <Tag className="w-4 h-4 text-[#D4AF37]" /> Additional Tracking Pixels
            </h3>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-[#A1A1AA] mb-1.5">
                  <BarChart3 className="w-3.5 h-3.5 text-[#EA4335]" /> Google Analytics ID
                </label>
                <input
                  data-testid="ga-id-input"
                  type="text"
                  value={settings.google_analytics_id || ""}
                  onChange={(e) => updateSetting("google_analytics_id", e.target.value)}
                  placeholder="G-XXXXXXXXXX"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-[#D4AF37] text-sm"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-[#A1A1AA] mb-1.5">
                  <Tag className="w-3.5 h-3.5 text-[#4285F4]" /> Google Tag Manager ID
                </label>
                <input
                  data-testid="gtm-id-input"
                  type="text"
                  value={settings.google_tag_manager_id || ""}
                  onChange={(e) => updateSetting("google_tag_manager_id", e.target.value)}
                  placeholder="GTM-XXXXXXX"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-[#D4AF37] text-sm"
                />
              </div>
              <div>
                <label className="flex items-center gap-2 text-xs font-medium text-[#A1A1AA] mb-1.5">
                  <svg className="w-3.5 h-3.5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M19.59 6.69a4.83 4.83 0 01-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 01-5.2 1.74 2.89 2.89 0 012.31-4.64 2.93 2.93 0 01.88.13V9.4a6.84 6.84 0 00-1-.05A6.33 6.33 0 005 20.1a6.34 6.34 0 0010.86-4.43v-7a8.16 8.16 0 004.77 1.52v-3.4a4.85 4.85 0 01-1-.1z"/>
                  </svg>
                  TikTok Pixel ID
                </label>
                <input
                  data-testid="tiktok-pixel-input"
                  type="text"
                  value={settings.tiktok_pixel_id || ""}
                  onChange={(e) => updateSetting("tiktok_pixel_id", e.target.value)}
                  placeholder="XXXXXXXXXXXXXXXXXX"
                  className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-[#D4AF37] text-sm"
                />
              </div>
            </div>
          </div>

          {/* Built-in Tracking */}
          <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
            <h3 className="font-semibold text-[#F5F5F0] mb-4 text-sm">Built-in Tracking</h3>
            <div className="space-y-3">
              {[
                { key: "track_clicks", label: "Track Clicks (Heatmap)", desc: "Record click positions for heatmap visualization" },
                { key: "track_scroll", label: "Track Scroll Depth", desc: "Monitor how far users scroll on the page" },
                { key: "track_time_on_page", label: "Track Time on Page", desc: "Measure engagement duration" },
              ].map(item => (
                <label key={item.key} className="flex items-center justify-between p-3 bg-[#0A0A0A] border border-[#27272A] rounded-md cursor-pointer hover:border-[#52525B] transition-colors">
                  <div>
                    <span className="text-[#F5F5F0] text-sm font-medium">{item.label}</span>
                    <p className="text-xs text-[#52525B]">{item.desc}</p>
                  </div>
                  <input
                    type="checkbox"
                    checked={settings[item.key] !== false}
                    onChange={(e) => updateSetting(item.key, e.target.checked)}
                    className="w-5 h-5 accent-[#D4AF37]"
                  />
                </label>
              ))}
            </div>
          </div>

          {/* Custom Scripts */}
          <div className="bg-[#121212] border border-[#27272A] rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-[#F5F5F0] text-sm">Custom Scripts</h3>
              <button
                onClick={addCustomScript}
                className="flex items-center gap-1 text-xs text-[#D4AF37] hover:text-[#B8943B]"
              >
                <Plus className="w-3.5 h-3.5" /> Add Script
              </button>
            </div>
            <div className="space-y-3">
              {(settings.custom_scripts || []).map((script, i) => (
                <div key={i} className="p-3 bg-[#0A0A0A] border border-[#27272A] rounded-md">
                  <div className="flex justify-between items-start mb-2 gap-2">
                    <input
                      type="text"
                      value={script.name}
                      onChange={(e) => updateCustomScript(i, { name: e.target.value })}
                      placeholder="Script name"
                      className="px-3 py-1.5 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded text-xs flex-1"
                    />
                    <div className="flex items-center gap-2">
                      <select
                        value={script.location}
                        onChange={(e) => updateCustomScript(i, { location: e.target.value })}
                        className="px-2 py-1.5 bg-[#121212] border border-[#27272A] text-[#F5F5F0] rounded text-xs"
                      >
                        <option value="head">Head</option>
                        <option value="body_start">Body Start</option>
                        <option value="body_end">Body End</option>
                      </select>
                      <button onClick={() => removeCustomScript(i)} className="p-1 text-red-400 hover:bg-red-500/10 rounded">
                        <Trash2 className="w-3.5 h-3.5" />
                      </button>
                    </div>
                  </div>
                  <textarea
                    value={script.code}
                    onChange={(e) => updateCustomScript(i, { code: e.target.value })}
                    placeholder="<script>...</script>"
                    rows={3}
                    className="w-full px-3 py-2 bg-[#121212] border border-[#27272A] text-[#A1A1AA] rounded font-mono text-xs"
                  />
                </div>
              ))}
              {(!settings.custom_scripts || settings.custom_scripts.length === 0) && (
                <p className="text-center text-[#52525B] py-4 text-sm">No custom scripts added</p>
              )}
            </div>
          </div>
        </div>
      ) : (
        <div className="text-center py-12 bg-[#121212] border border-[#27272A] rounded-lg">
          <Code className="w-10 h-10 text-[#3f3f46] mx-auto mb-3" />
          <p className="text-[#52525B] text-sm">Select a landing page to configure per-page tracking</p>
        </div>
      )}
    </motion.div>
  );
};

// ─── Tracking Structure Panel ───
const TrackingStructure = () => {
  const pages = [
    {
      path: "/",
      name: "Homepage",
      events: [
        { name: "PageView", trigger: "Page load", browser: true, server: true },
        { name: "ViewContent", trigger: "Page load", browser: true, server: true },
      ],
    },
    {
      path: "/suds-1, /suds-2, /:slug",
      name: "Event Landing Pages",
      events: [
        { name: "PageView", trigger: "Page load / route change", browser: true, server: true },
        { name: "ViewContent", trigger: "Page data loads", browser: true, server: true },
        { name: "InitiateCheckout", trigger: "User submits checkout form", browser: true, server: true },
        { name: "Purchase", trigger: "Payment confirmed by Stripe", browser: true, server: true, dedup: true },
      ],
    },
    {
      path: "/events/success",
      name: "Event Booking Success",
      events: [
        { name: "Purchase", trigger: "payment_status === 'paid'", browser: true, server: true, dedup: true },
      ],
    },
    {
      path: "/booking",
      name: "Experience Booking",
      events: [
        { name: "InitiateCheckout", trigger: "User submits booking form", browser: true, server: true },
      ],
    },
    {
      path: "/booking/success",
      name: "Experience Booking Success",
      events: [
        { name: "Purchase", trigger: "payment_status === 'paid'", browser: true, server: true, dedup: true },
      ],
    },
    {
      path: "/contact",
      name: "Contact Page",
      events: [
        { name: "Lead", trigger: "Form submission", browser: true, server: true },
      ],
    },
    {
      path: "/mocktails",
      name: "Mocktails Menu",
      events: [
        { name: "ViewContent", trigger: "Page load", browser: true, server: true },
      ],
    },
    {
      path: "All pages",
      name: "Site-wide (SPA Router)",
      events: [
        { name: "PageView", trigger: "Every route change", browser: true, server: true },
      ],
    },
  ];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.12 }}
      className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden"
    >
      <div className="p-5 border-b border-[#27272A]">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-[#D4AF37]/10 flex items-center justify-center">
            <Zap className="w-4.5 h-4.5 text-[#D4AF37]" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#F5F5F0]" data-testid="tracking-structure-title">Tracking Structure</h2>
            <p className="text-xs text-[#52525B]">One pixel, site-wide. Browser + Server CAPI with deduplication.</p>
          </div>
        </div>
      </div>

      {/* Architecture summary */}
      <div className="px-5 py-3 border-b border-[#1a1a1a] grid sm:grid-cols-3 gap-3">
        <div className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-blue-400 mt-1.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-[#F5F5F0]">Browser Pixel</p>
            <p className="text-xs text-[#52525B]">fbevents.js loaded dynamically from CMS config</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-purple-400 mt-1.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-[#F5F5F0]">Server CAPI</p>
            <p className="text-xs text-[#52525B]">POST /api/meta/track with hashed PII, IP, user-agent</p>
          </div>
        </div>
        <div className="flex items-start gap-2">
          <div className="w-1.5 h-1.5 rounded-full bg-green-400 mt-1.5 shrink-0" />
          <div>
            <p className="text-xs font-medium text-[#F5F5F0]">Deduplication</p>
            <p className="text-xs text-[#52525B]">Same event_id in Pixel + CAPI. Purchase uses purchase_{`{id}`}</p>
          </div>
        </div>
      </div>

      {/* Event plan table */}
      <div className="overflow-x-auto">
        <table className="w-full text-xs" data-testid="tracking-structure-table">
          <thead>
            <tr className="border-b border-[#1a1a1a] text-[#52525B]">
              <th className="text-left px-5 py-2.5 font-medium">Page / Route</th>
              <th className="text-left px-3 py-2.5 font-medium">Event</th>
              <th className="text-left px-3 py-2.5 font-medium">Trigger</th>
              <th className="text-center px-3 py-2.5 font-medium">Browser</th>
              <th className="text-center px-3 py-2.5 font-medium">Server</th>
              <th className="text-center px-3 py-2.5 font-medium">Dedup</th>
            </tr>
          </thead>
          <tbody>
            {pages.map((pg) =>
              pg.events.map((ev, ei) => (
                <tr key={`${pg.path}-${ev.name}`} className="border-b border-[#0f0f0f] hover:bg-[#0A0A0A]">
                  {ei === 0 ? (
                    <td className="px-5 py-2 align-top" rowSpan={pg.events.length}>
                      <span className="text-[#F5F5F0] font-medium block">{pg.name}</span>
                      <span className="text-[#3f3f46] font-mono">{pg.path}</span>
                    </td>
                  ) : null}
                  <td className="px-3 py-2">
                    <span className="px-2 py-0.5 rounded bg-[#27272A] text-[#D4AF37] font-mono">{ev.name}</span>
                  </td>
                  <td className="px-3 py-2 text-[#A1A1AA]">{ev.trigger}</td>
                  <td className="px-3 py-2 text-center">
                    {ev.browser && <Check className="w-3.5 h-3.5 text-blue-400 mx-auto" />}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ev.server && <Check className="w-3.5 h-3.5 text-purple-400 mx-auto" />}
                  </td>
                  <td className="px-3 py-2 text-center">
                    {ev.dedup && <Shield className="w-3.5 h-3.5 text-green-400 mx-auto" />}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Key rules */}
      <div className="px-5 py-3 border-t border-[#1a1a1a] space-y-1.5">
        <p className="text-xs text-[#52525B] flex items-start gap-2">
          <Shield className="w-3 h-3 text-green-400 mt-0.5 shrink-0" />
          <span><strong className="text-[#A1A1AA]">Purchase</strong> fires only after payment_status === 'paid'. Server also fires independently on Stripe webhook for redundancy.</span>
        </p>
        <p className="text-xs text-[#52525B] flex items-start gap-2">
          <Zap className="w-3 h-3 text-[#D4AF37] mt-0.5 shrink-0" />
          <span><strong className="text-[#A1A1AA]">SPA Route Changes</strong> handled by RouteChangeTracker — PageView fires on every navigation, not just initial load.</span>
        </p>
        <p className="text-xs text-[#52525B] flex items-start gap-2">
          <Activity className="w-3 h-3 text-blue-400 mt-0.5 shrink-0" />
          <span><strong className="text-[#A1A1AA]">Event Queue</strong> — events are buffered until CMS config loads, then replayed. No events are lost during initial page load.</span>
        </p>
      </div>
    </motion.div>
  );
};

// ─── Meta Events Activity Log ───
const MetaEventsLog = () => {
  const [events, setEvents] = useState([]);
  const [stats, setStats] = useState({ total: 0, success: 0, failed: 0 });
  const [loading, setLoading] = useState(true);
  const [filterEvent, setFilterEvent] = useState("");
  const [filterStatus, setFilterStatus] = useState("");
  const [filterSource, setFilterSource] = useState("");

  const fetchLog = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "100" });
      if (filterEvent) params.append("event_name", filterEvent);
      if (filterStatus) params.append("status", filterStatus);
      if (filterSource) params.append("source", filterSource);
      const res = await axios.get(`${API}/admin/meta-events-log?${params}`);
      setEvents(res.data.events || []);
      setStats(res.data.stats || { total: 0, success: 0, failed: 0 });
    } catch {
      toast.error("Failed to load events log");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchLog(); }, [filterEvent, filterStatus, filterSource]);

  const clearLog = async () => {
    if (!window.confirm("Clear all activity log entries?")) return;
    try {
      await axios.delete(`${API}/admin/meta-events-log`);
      toast.success("Activity log cleared");
      fetchLog();
    } catch {
      toast.error("Failed to clear log");
    }
  };

  const formatTime = (ts) => {
    if (!ts) return "—";
    const d = new Date(ts);
    return d.toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: false });
  };

  const sourceLabel = (s) => {
    const map = { browser_capi: "Browser", server_purchase: "Server", test_connection: "Test", playground: "Playground" };
    return map[s] || s;
  };

  const sourceColor = (s) => {
    const map = { browser_capi: "text-blue-400 bg-blue-500/10 border-blue-500/20", server_purchase: "text-purple-400 bg-purple-500/10 border-purple-500/20", test_connection: "text-amber-400 bg-amber-500/10 border-amber-500/20", playground: "text-cyan-400 bg-cyan-500/10 border-cyan-500/20" };
    return map[s] || "text-[#71717A] bg-[#27272A] border-[#3f3f46]";
  };

  const hasFilters = filterEvent || filterStatus || filterSource;

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.15 }}
      className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden"
    >
      {/* Header */}
      <div className="p-5 border-b border-[#27272A] flex flex-col sm:flex-row justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-green-500/10 flex items-center justify-center">
            <Activity className="w-4.5 h-4.5 text-green-400" />
          </div>
          <div>
            <h2 className="text-base font-semibold text-[#F5F5F0]" data-testid="events-log-title">Events Activity Log</h2>
            <p className="text-xs text-[#52525B]">Recent CAPI events sent to Meta</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {/* Stats pills */}
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-[#27272A] text-[#A1A1AA] border border-[#3f3f46]" data-testid="stat-total">
            {stats.total} total
          </span>
          <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-green-500/10 text-green-400 border border-green-500/20" data-testid="stat-success">
            {stats.success} sent
          </span>
          {stats.failed > 0 && (
            <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-red-500/10 text-red-400 border border-red-500/20" data-testid="stat-failed">
              {stats.failed} failed
            </span>
          )}
          <button onClick={fetchLog} className="p-1.5 text-[#52525B] hover:text-[#A1A1AA] transition-colors" title="Refresh">
            <RefreshCw className="w-4 h-4" />
          </button>
        </div>
      </div>

      {/* Filters */}
      <div className="px-5 py-3 border-b border-[#1a1a1a] flex flex-wrap items-center gap-2">
        <Filter className="w-3.5 h-3.5 text-[#52525B]" />
        <select
          data-testid="filter-event"
          value={filterEvent}
          onChange={(e) => setFilterEvent(e.target.value)}
          className="px-2.5 py-1 text-xs bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] rounded-md"
        >
          <option value="">All Events</option>
          <option value="PageView">PageView</option>
          <option value="ViewContent">ViewContent</option>
          <option value="InitiateCheckout">InitiateCheckout</option>
          <option value="Purchase">Purchase</option>
          <option value="Lead">Lead</option>
          <option value="AddToCart">AddToCart</option>
        </select>
        <select
          data-testid="filter-status"
          value={filterStatus}
          onChange={(e) => setFilterStatus(e.target.value)}
          className="px-2.5 py-1 text-xs bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] rounded-md"
        >
          <option value="">All Status</option>
          <option value="success">Success</option>
          <option value="failed">Failed</option>
        </select>
        <select
          data-testid="filter-source"
          value={filterSource}
          onChange={(e) => setFilterSource(e.target.value)}
          className="px-2.5 py-1 text-xs bg-[#0A0A0A] border border-[#27272A] text-[#A1A1AA] rounded-md"
        >
          <option value="">All Sources</option>
          <option value="browser_capi">Browser</option>
          <option value="server_purchase">Server</option>
          <option value="test_connection">Test</option>
          <option value="playground">Playground</option>
        </select>
        {hasFilters && (
          <button
            onClick={() => { setFilterEvent(""); setFilterStatus(""); setFilterSource(""); }}
            className="px-2 py-1 text-xs text-[#D4AF37] hover:text-[#B8943B] flex items-center gap-1"
          >
            <X className="w-3 h-3" /> Clear
          </button>
        )}
        <div className="ml-auto">
          <button
            data-testid="clear-log-btn"
            onClick={clearLog}
            disabled={events.length === 0}
            className="px-2.5 py-1 text-xs text-red-400 hover:bg-red-500/10 rounded-md transition-colors disabled:opacity-30 disabled:cursor-not-allowed flex items-center gap-1"
          >
            <Trash2 className="w-3 h-3" /> Clear Log
          </button>
        </div>
      </div>

      {/* Table */}
      <div className="overflow-x-auto">
        {loading ? (
          <div className="flex items-center justify-center py-10">
            <div className="animate-spin rounded-full h-6 w-6 border-2 border-[#D4AF37] border-t-transparent" />
          </div>
        ) : events.length === 0 ? (
          <div className="text-center py-10">
            <Activity className="w-8 h-8 text-[#27272A] mx-auto mb-2" />
            <p className="text-[#52525B] text-sm">{hasFilters ? "No events match filters" : "No events recorded yet"}</p>
            <p className="text-[#3f3f46] text-xs mt-1">Events appear here when Meta tracking is enabled and visitors interact with your pages</p>
          </div>
        ) : (
          <table className="w-full text-xs" data-testid="events-log-table">
            <thead>
              <tr className="border-b border-[#1a1a1a] text-[#52525B]">
                <th className="text-left px-5 py-2.5 font-medium">Event</th>
                <th className="text-left px-3 py-2.5 font-medium">Status</th>
                <th className="text-left px-3 py-2.5 font-medium">Source</th>
                <th className="text-left px-3 py-2.5 font-medium hidden md:table-cell">Page</th>
                <th className="text-right px-5 py-2.5 font-medium">Time</th>
              </tr>
            </thead>
            <tbody>
              {events.map((ev, i) => {
                const pagePath = ev.event_source_url ? new URL(ev.event_source_url).pathname : "—";
                return (
                  <tr
                    key={`${ev.event_id}-${i}`}
                    className="border-b border-[#0f0f0f] hover:bg-[#0A0A0A] transition-colors"
                  >
                    <td className="px-5 py-2.5">
                      <span className="text-[#F5F5F0] font-medium">{ev.event_name}</span>
                    </td>
                    <td className="px-3 py-2.5">
                      {ev.status === "success" ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-green-500/10 text-green-400 border border-green-500/20">
                          <Check className="w-3 h-3" /> Sent
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-500/10 text-red-400 border border-red-500/20">
                          <AlertCircle className="w-3 h-3" /> Failed
                        </span>
                      )}
                    </td>
                    <td className="px-3 py-2.5">
                      <span className={`inline-flex items-center px-2 py-0.5 rounded-full border text-xs ${sourceColor(ev.source)}`}>
                        {sourceLabel(ev.source)}
                      </span>
                    </td>
                    <td className="px-3 py-2.5 text-[#71717A] hidden md:table-cell max-w-[200px] truncate" title={pagePath}>
                      {pagePath}
                    </td>
                    <td className="px-5 py-2.5 text-right text-[#71717A] whitespace-nowrap">
                      <span className="inline-flex items-center gap-1">
                        <Clock className="w-3 h-3" /> {formatTime(ev.timestamp)}
                      </span>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </motion.div>
  );
};

// ─── Meta CAPI Event Test Playground ───
const EVENT_TEMPLATES = {
  PageView: {
    description: "Fires on every page load / SPA route change",
    fields: { event_source_url: "" },
    custom_data: { content_name: "", landing_page_path: "/" },
    user_data: {},
  },
  ViewContent: {
    description: "Fires when a visitor views an event landing page",
    fields: { event_source_url: "" },
    custom_data: { content_name: "", content_category: "Event", content_ids: "", content_type: "product", value: "0", currency: "CAD" },
    user_data: {},
  },
  Lead: {
    description: "Fires on contact form submission",
    fields: { event_source_url: "" },
    custom_data: { content_name: "contact_form", content_category: "lead" },
    user_data: { email: "", phone: "", first_name: "" },
  },
  InitiateCheckout: {
    description: "Fires when a visitor starts the checkout / booking flow",
    fields: { event_source_url: "" },
    custom_data: { content_name: "", content_category: "events", content_ids: "", value: "0", currency: "CAD", num_items: "1" },
    user_data: { email: "" },
  },
  Purchase: {
    description: "Fires ONLY after confirmed payment — never on click or page load",
    fields: { event_source_url: "" },
    custom_data: { content_name: "", value: "0", currency: "CAD", order_id: "", num_items: "1", content_category: "events" },
    user_data: { email: "", phone: "", first_name: "" },
  },
};

const MetaTestPlayground = () => {
  const [selectedEvent, setSelectedEvent] = useState("PageView");
  const [customData, setCustomData] = useState({ ...EVENT_TEMPLATES.PageView.custom_data });
  const [userData, setUserData] = useState({ ...EVENT_TEMPLATES.PageView.user_data });
  const [sourceUrl, setSourceUrl] = useState("");
  const [testCodeOverride, setTestCodeOverride] = useState("");
  const [firing, setFiring] = useState(false);
  const [result, setResult] = useState(null);
  const [history, setHistory] = useState([]);
  const [expanded, setExpanded] = useState(true);

  const switchEvent = (name) => {
    setSelectedEvent(name);
    setCustomData({ ...EVENT_TEMPLATES[name].custom_data });
    setUserData({ ...EVENT_TEMPLATES[name].user_data });
    setResult(null);
  };

  const fireEvent = async () => {
    setFiring(true);
    setResult(null);
    try {
      const payload = {
        event_name: selectedEvent,
        event_source_url: sourceUrl || undefined,
        test_event_code_override: testCodeOverride || undefined,
        custom_data: Object.fromEntries(
          Object.entries(customData).filter(([, v]) => v !== "").map(([k, v]) => {
            if (k === "content_ids" && typeof v === "string") return [k, v.split(",").map(s => s.trim()).filter(Boolean)];
            if (["value", "num_items"].includes(k)) return [k, parseFloat(v) || 0];
            return [k, v];
          })
        ),
        user_data: Object.fromEntries(Object.entries(userData).filter(([, v]) => v !== "")),
      };
      const res = await axios.post(`${API}/admin/meta-playground/fire`, payload);
      setResult(res.data);
      setHistory(prev => [{ ...res.data, timestamp: new Date().toISOString() }, ...prev].slice(0, 10));
      if (res.data.success) toast.success(`${selectedEvent} sent — events_received: ${res.data.events_received}`);
      else toast.error(res.data.error || "Event failed");
    } catch {
      toast.error("Failed to fire event");
      setResult({ success: false, error: "Request failed" });
    } finally {
      setFiring(false);
    }
  };

  const copyEventId = () => {
    if (result?.event_id) {
      navigator.clipboard.writeText(result.event_id);
      toast.success("Event ID copied");
    }
  };

  const tpl = EVENT_TEMPLATES[selectedEvent];

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ delay: 0.08 }}
      className="bg-[#121212] border border-[#27272A] rounded-lg overflow-hidden"
    >
      {/* Header */}
      <button
        onClick={() => setExpanded(p => !p)}
        className="w-full p-5 border-b border-[#27272A] flex items-center justify-between hover:bg-[#0A0A0A]/50 transition-colors"
        data-testid="playground-toggle"
      >
        <div className="flex items-center gap-3">
          <div className="w-9 h-9 rounded-lg bg-cyan-500/10 flex items-center justify-center">
            <FlaskConical className="w-4.5 h-4.5 text-cyan-400" />
          </div>
          <div className="text-left">
            <h2 className="text-base font-semibold text-[#F5F5F0]" data-testid="playground-title">CAPI Event Test Playground</h2>
            <p className="text-xs text-[#52525B]">Fire test events to Meta and see live responses. Events use your Test Event Code.</p>
          </div>
        </div>
        {expanded ? <ChevronUp className="w-4 h-4 text-[#52525B]" /> : <ChevronDown className="w-4 h-4 text-[#52525B]" />}
      </button>

      {expanded && (
        <div className="p-5 space-y-5">
          {/* Event Type Selector */}
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-2 block">Event Type</label>
            <div className="flex flex-wrap gap-2" data-testid="event-type-selector">
              {Object.keys(EVENT_TEMPLATES).map(name => (
                <button
                  key={name}
                  onClick={() => switchEvent(name)}
                  data-testid={`event-btn-${name}`}
                  className={`px-3 py-1.5 rounded-md text-xs font-medium transition-all ${
                    selectedEvent === name
                      ? "bg-cyan-500/15 text-cyan-300 border border-cyan-500/30 shadow-[0_0_8px_rgba(6,182,212,0.1)]"
                      : "bg-[#0A0A0A] text-[#71717A] border border-[#27272A] hover:border-[#52525B] hover:text-[#A1A1AA]"
                  }`}
                >
                  {name}
                </button>
              ))}
            </div>
            <p className="text-xs text-[#52525B] mt-2 flex items-center gap-1.5">
              <AlertCircle className="w-3 h-3 shrink-0" />
              {tpl.description}
            </p>
          </div>

          {/* Source URL */}
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 block">event_source_url</label>
            <input
              data-testid="playground-source-url"
              type="text"
              value={sourceUrl}
              onChange={e => setSourceUrl(e.target.value)}
              placeholder="https://mixandmingleevents.com/suds-1"
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-cyan-500/40 text-sm font-mono placeholder:text-[#3f3f46]"
            />
          </div>

          {/* Test Event Code Override */}
          <div>
            <label className="text-xs font-medium text-[#A1A1AA] mb-1.5 flex items-center gap-2">
              Test Event Code
              <span className="text-[#3f3f46] font-normal">(paste fresh code from Events Manager &gt; Test Events)</span>
            </label>
            <input
              data-testid="playground-test-code"
              type="text"
              value={testCodeOverride}
              onChange={e => setTestCodeOverride(e.target.value.trim())}
              placeholder={`Leave blank to use env var`}
              className="w-full px-3 py-2 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded-md focus:outline-none focus:border-amber-500/40 text-sm font-mono placeholder:text-[#3f3f46]"
            />
          </div>

          {/* Custom Data Fields */}
          {Object.keys(customData).length > 0 && (
            <div>
              <label className="text-xs font-medium text-[#A1A1AA] mb-2 block">custom_data</label>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(customData).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs text-[#52525B] font-mono w-32 shrink-0 truncate" title={key}>{key}</span>
                    <input
                      data-testid={`cd-${key}`}
                      type="text"
                      value={val}
                      onChange={e => setCustomData(prev => ({ ...prev, [key]: e.target.value }))}
                      className="flex-1 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded text-xs font-mono focus:outline-none focus:border-cyan-500/40 placeholder:text-[#3f3f46]"
                      placeholder={key}
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* User Data Fields */}
          {Object.keys(userData).length > 0 && (
            <div>
              <label className="text-xs font-medium text-[#A1A1AA] mb-2 flex items-center gap-1.5">
                user_data
                <span className="text-[#52525B] font-normal">(hashed before sending)</span>
              </label>
              <div className="grid sm:grid-cols-2 gap-2">
                {Object.entries(userData).map(([key, val]) => (
                  <div key={key} className="flex items-center gap-2">
                    <span className="text-xs text-[#52525B] font-mono w-32 shrink-0">{key}</span>
                    <input
                      data-testid={`ud-${key}`}
                      type="text"
                      value={val}
                      onChange={e => setUserData(prev => ({ ...prev, [key]: e.target.value }))}
                      placeholder={key === "email" ? "test@example.com" : key === "phone" ? "4165551234" : ""}
                      className="flex-1 px-2.5 py-1.5 bg-[#0A0A0A] border border-[#27272A] text-[#F5F5F0] rounded text-xs font-mono focus:outline-none focus:border-cyan-500/40 placeholder:text-[#3f3f46]"
                    />
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Fire Button */}
          <div className="flex items-center gap-3">
            <button
              data-testid="playground-fire-btn"
              onClick={fireEvent}
              disabled={firing}
              className="flex items-center gap-2 px-5 py-2 bg-cyan-600 text-white font-semibold text-sm rounded-md hover:bg-cyan-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Play className="w-4 h-4" />
              {firing ? "Sending..." : `Fire ${selectedEvent}`}
            </button>
            <span className="text-xs text-[#3f3f46]">Server-side CAPI only. No browser pixel fired.</span>
          </div>

          {/* Result Panel */}
          {result && (
            <div
              data-testid="playground-result"
              className={`p-4 rounded-md border text-sm font-mono ${
                result.success
                  ? "bg-green-500/5 border-green-500/20"
                  : "bg-red-500/5 border-red-500/20"
              }`}
            >
              <div className="flex items-start justify-between gap-2 mb-3">
                <div className="flex items-center gap-2">
                  {result.success ? (
                    <Check className="w-4 h-4 text-green-400 shrink-0" />
                  ) : (
                    <AlertCircle className="w-4 h-4 text-red-400 shrink-0" />
                  )}
                  <span className={result.success ? "text-green-300" : "text-red-300"}>
                    {result.success ? "Event accepted by Meta" : "Event failed"}
                  </span>
                </div>
                {result.event_id && (
                  <button
                    onClick={copyEventId}
                    className="flex items-center gap-1 text-xs text-[#52525B] hover:text-[#A1A1AA] transition-colors"
                    title="Copy event_id for dedup testing"
                  >
                    <Copy className="w-3 h-3" /> Copy ID
                  </button>
                )}
              </div>
              <div className="space-y-1 text-xs">
                <div className="flex gap-2">
                  <span className="text-[#52525B] w-28 shrink-0">event_name</span>
                  <span className="text-[#F5F5F0]">{result.event_name}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-[#52525B] w-28 shrink-0">event_id</span>
                  <span className="text-cyan-400">{result.event_id}</span>
                </div>
                <div className="flex gap-2">
                  <span className="text-[#52525B] w-28 shrink-0">events_received</span>
                  <span className="text-[#F5F5F0]">{result.events_received}</span>
                </div>
                {result.pixel_id && (
                  <div className="flex gap-2">
                    <span className="text-[#52525B] w-28 shrink-0">pixel_id</span>
                    <span className="text-[#A1A1AA] font-mono">{result.pixel_id}</span>
                  </div>
                )}
                {result.test_event_code && (
                  <div className="flex gap-2">
                    <span className="text-[#52525B] w-28 shrink-0">test_code</span>
                    <span className="text-amber-400">{result.test_event_code}</span>
                  </div>
                )}
                {result.fbtrace_id && (
                  <div className="flex gap-2">
                    <span className="text-[#52525B] w-28 shrink-0">fbtrace_id</span>
                    <span className="text-[#71717A] font-mono">{result.fbtrace_id}</span>
                  </div>
                )}
                {result.error && (
                  <div className="flex gap-2">
                    <span className="text-[#52525B] w-28 shrink-0">error</span>
                    <span className="text-red-400 break-all">{result.error}</span>
                  </div>
                )}
                {result.messages && result.messages.length > 0 && (
                  <div className="mt-2 p-2 bg-amber-500/5 border border-amber-500/20 rounded">
                    <span className="text-amber-400 text-xs font-medium block mb-1">Meta Warnings:</span>
                    {result.messages.map((msg, i) => (
                      <p key={i} className="text-amber-300/80 text-xs">{typeof msg === 'string' ? msg : JSON.stringify(msg)}</p>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}

          {/* History */}
          {history.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-medium text-[#52525B]">Session History ({history.length})</label>
                <button
                  onClick={() => setHistory([])}
                  className="text-xs text-[#3f3f46] hover:text-[#71717A] transition-colors"
                  data-testid="playground-clear-history"
                >
                  Clear
                </button>
              </div>
              <div className="space-y-1">
                {history.map((h, i) => (
                  <div
                    key={i}
                    className="flex items-center gap-3 px-3 py-1.5 bg-[#0A0A0A] border border-[#1a1a1a] rounded text-xs"
                  >
                    {h.success ? (
                      <Check className="w-3 h-3 text-green-400 shrink-0" />
                    ) : (
                      <AlertCircle className="w-3 h-3 text-red-400 shrink-0" />
                    )}
                    <span className="text-[#F5F5F0] font-medium w-28">{h.event_name}</span>
                    <span className="text-cyan-400/70 font-mono truncate flex-1" title={h.event_id}>{h.event_id}</span>
                    <span className="text-[#3f3f46] whitespace-nowrap">
                      {h.timestamp ? new Date(h.timestamp).toLocaleTimeString("en-US", { hour12: false }) : ""}
                    </span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
};

// ─── Main Page ───
const PixelSettings = () => {
  return (
    <AdminLayout>
      <div className="p-6 lg:p-8 max-w-4xl">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-[#F5F5F0] flex items-center gap-2" data-testid="pixel-settings-heading">
            <Code className="w-7 h-7 text-[#D4AF37]" />
            Pixel & Tracking Settings
          </h1>
          <p className="text-[#71717A] mt-1 text-sm">Configure Meta Pixel, CAPI, and additional tracking for your landing pages</p>
        </div>

        <div className="space-y-8">
          {/* Global Meta Config */}
          <MetaGlobalConfig />

          {/* CAPI Test Playground */}
          <MetaTestPlayground />

          {/* Events Activity Log */}
          <MetaEventsLog />

          {/* Tracking Structure */}
          <TrackingStructure />

          {/* Per-Page Settings */}
          <div>
            <h2 className="text-base font-semibold text-[#F5F5F0] mb-4">Per-Page Settings</h2>
            <PerPagePixelSettings />
          </div>
        </div>
      </div>
    </AdminLayout>
  );
};

export default PixelSettings;
